import { useActionLogStatsQuery, useActionLogsQuery } from '@app/api/dieter/actionLog';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { UserRole } from '@dieterApi/user/useUserQuery';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export function Stats() {
  const { user } = useUserWithCompany();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
    endDate: new Date().toISOString(),
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { data: logsData, loading: logsLoading, error: logsError, refetch: refetchLogs } = useActionLogsQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limit: 50,
  });

  const { data: statsData, loading: statsLoading, error: statsError, refetch: refetchStats } = useActionLogStatsQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const isAdmin = user?.role === UserRole.Admin;

  const toggleRow = (logId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAction = (action: string) => {
    const parts = action.split('.');
    return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  };

  useEffect(() => {
    refetchLogs();
    refetchStats();
  }, [dateRange]);

  if (!isAdmin) {
    return (
      <div className="2xl:container flex flex-col p-6">
        <h1 className="text-2xl font-bold mb-4">Action Log Statistics</h1>
        <p className="text-lg text-red-600">You need admin permissions to view this page.</p>
        <div className="mt-4">
          <Link to="/dashboard" className="text-blue-600 hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="2xl:container flex flex-col p-6">
      <h1 className="text-2xl font-bold mb-4">Action Log Statistics</h1>

      <div className="mb-6">
        <Link to="/dashboard" className="text-blue-600 hover:underline">
          ← Back to Dashboard
        </Link>
      </div>

      {/* Date Range Filter */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h2 className="text-lg font-semibold mb-3">Filter by Date Range</h2>
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate.split('T')[0]}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, startDate: new Date(e.target.value).toISOString() }))
              }
              className="border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate.split('T')[0]}
              onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: new Date(e.target.value).toISOString() }))}
              className="border rounded px-3 py-2"
            />
          </div>
          <button
            onClick={() => {
              setDateRange({
                startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                endDate: new Date().toISOString(),
              });
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Last 7 Days
          </button>
          <button
            onClick={() => {
              setDateRange({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
                endDate: new Date().toISOString(),
              });
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Last 30 Days
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {!statsLoading && statsData?.actionLogStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Actions */}
          <div className="p-4 bg-white border rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Actions</h3>
            <p className="text-3xl font-bold">
              {statsData.actionLogStats.actionCounts.reduce((sum, item) => sum + item._count, 0)}
            </p>
          </div>

          {/* Active Users */}
          <div className="p-4 bg-white border rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Active Users</h3>
            <p className="text-3xl font-bold">{statsData.actionLogStats.userCounts.length}</p>
          </div>

          {/* Manual vs Automated */}
          <div className="p-4 bg-white border rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Manual vs Automated</h3>
            <div className="text-sm">
              {statsData.actionLogStats.automatedCounts.map((item) => (
                <div key={String(item.automated)} className="flex justify-between">
                  <span>{item.automated ? 'Automated:' : 'Manual:'}</span>
                  <span className="font-semibold">{item._count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action Types Table */}
      {!statsLoading && statsData?.actionLogStats && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-3">Actions by Type</h2>
          <div className="overflow-x-auto bg-white border rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {statsData.actionLogStats.actionCounts
                  .sort((a, b) => b._count - a._count)
                  .map((item) => (
                    <tr key={item.action}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatAction(item.action)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item._count}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Most Active Users Table */}
      {!statsLoading && statsData?.actionLogStats && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-3">Most Active Users</h2>
          <div className="overflow-x-auto bg-white border rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {statsData.actionLogStats.userCounts
                  .sort((a, b) => b._count - a._count)
                  .slice(0, 10)
                  .map((item) => (
                    <tr key={item.userId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.userName || item.userEmail}</div>
                        {item.userName && <div className="text-xs text-gray-500">{item.userEmail}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item._count}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Action Logs */}
      <div>
        <h2 className="text-xl font-semibold mb-3">Recent Actions</h2>
        {logsLoading ? (
          <p>Loading action logs...</p>
        ) : (
          <div className="overflow-x-auto bg-white border rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    {/* Expand button column */}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logsData?.actionLogs && logsData.actionLogs.length > 0 ? (
                  logsData.actionLogs.map((log) => (
                    <>
                      <tr
                        key={log.id}
                        className={`${log.automated ? 'bg-gray-50' : ''} cursor-pointer hover:bg-gray-100`}
                        onClick={() => toggleRow(log.id)}
                      >
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button className="text-gray-400 hover:text-gray-600">
                            {expandedRows.has(log.id) ? (
                              <span className="text-lg">▼</span>
                            ) : (
                              <span className="text-lg">▶</span>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{log.userName || log.userEmail}</div>
                          {log.userName && <div className="text-xs text-gray-500">{log.userEmail}</div>}
                          {!log.userId && <div className="text-xs text-gray-400 italic">(external)</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {formatAction(log.action)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>{log.resourceName || log.resourceId}</div>
                          {log.resourceName && <div className="text-xs text-gray-500">{log.resourceId}</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>{log.resourceType}</div>
                          {log.automated && <div className="text-xs text-gray-400">Automated</div>}
                        </td>
                      </tr>
                      {expandedRows.has(log.id) && (
                        <tr key={`${log.id}-details`} className="bg-gray-50">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="grid grid-cols-2 gap-4">
                              {log.beforeState && (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Before:</h4>
                                  <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-auto max-h-96">
                                    {JSON.stringify(log.beforeState, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log.afterState && (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">After:</h4>
                                  <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-auto max-h-96">
                                    {JSON.stringify(log.afterState, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {!log.beforeState && !log.afterState && (
                                <p className="text-sm text-gray-500 italic col-span-2">No state changes recorded.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No action logs found for the selected period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
